import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Popover } from "antd";

import { FaLocationDot } from "react-icons/fa6";

import { WatchlistAction } from "../../store/actions";

import UniversityInfoModal from "./university-info-modal";
import { Images } from "../../config";

import DefaultButton from "../buttons/default-button";
import WhiteButton from "../buttons/white-button";

import "./styles.css";

const GridViewCard = (props) => {
  const dispatch = useDispatch();
  const cardRef = useRef(null);

  const { data, setIsPopoverOpen, containerRef } = props;
  const profileData = useSelector((state) => state?.profile?.user);

  const [showModal, setShowModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.intersectionRatio >= 0.9);
      },
      {
        root: containerRef.current,
        rootMargin: "0px",
        threshold: [0.9],
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [containerRef]);

  const handleShortlist = () => {
    const payload = { id: data?.id };
    dispatch(
      WatchlistAction.addOrRemoveToWishlist(payload, (res) => {
        dispatch(WatchlistAction.getWatchlist({}));
      })
    );
  };

  const popoverContent = (
    <div className="university-grid">
      <div
        className="w-full rounded-t-lg relative min-h-[8.95em] cursor-pointer"
        style={{
          background: `url(${data?.campusPic ?? Images.DefaultUniCover})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        onClick={() => setShowModal(true)}
      >
        <div className="absolute inset-0 bg-black opacity-50 rounded-t-lg"></div>
        <div className="relative p-3 flex flex-col gap-2 overflow-y-auto text-white">
          <img
            src={data?.profilePic ?? Images.LogoIconOnly}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = Images.LogoIconOnly;
            }}
            className="h-[2.625em] w-[2.625em] border border-searchBorder shadow-boxContainer rounded-full object-contain bg-white"
          />
          <div className="flex flex-col gap-2">
            <div className="font-bold text-[0.975em] line-clamp-2 overflow-hidden text-ellipsis">
              {data?.name}
            </div>
            <div className="flex gap-1 items-center font-semibold">
              <div className="text-[0.775em] text-white">
                <FaLocationDot />
              </div>
              <div className="flex items-center text-[0.675em]">
                {data?.city && `${data?.city}`}
                {data?.state && `, ${data?.state}`}
                {data?.country?.name && `, ${data?.country.name}`}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex flex-col p-2 py-3 gap-2 cursor-pointer"
        onClick={() => setShowModal(true)}
      >
        <div className="grid-popover-value-container">
          <div>Average Course Duration:</div>
          {data?.courseDuration ? (
            <div className="grid-popover-value">{data?.courseDuration}</div>
          ) : (
            "NA"
          )}
        </div>
        <div className="grid-popover-value-container">
          <div>Average Fee:</div>
          {data?.avgFeesUSD ? (
            <div className="grid-popover-value">
              USD {data?.avgFeesUSD?.toLocaleString()}
            </div>
          ) : (
            "NA"
          )}
        </div>
        <div className="grid-popover-value-container">
          <div>Acceptance Rate:</div>
          {data?.acceptanceRate ? (
            <div className="grid-popover-value">
              {(data?.acceptanceRate * 100).toFixed(1)}%
            </div>
          ) : (
            "NA"
          )}
        </div>
        <div className="grid-popover-value-container">
          <div>Application Status:</div>
          {data?.appDeadline === "NA" ? (
            "NA"
          ) : (
            <span
              className={`${
                data?.appDeadline === "closed" ? "bg-red-500" : "bg-green-500"
              } text-white font-medium px-1 pt-1 pb-0.5 rounded-md ml-1 text-[0.825em]`}
            >
              {data?.appDeadline === "closed" ? "CLOSED" : "OPEN"}
            </span>
          )}
        </div>
        <div className="grid-popover-value-container">
          <div>Avg. Fresh Grad Salary:</div>
          {data?.freshGradSalary ? (
            <span className="grid-popover-value">
              USD {data?.freshGradSalary.toLocaleString()}
            </span>
          ) : (
            "NA"
          )}
        </div>
      </div>
      <div className="flex p-2 pb-3 pt-0 gap-2">
        <Link className="w-full" to={data?.appLink ?? "#"} target="_blank">
          <DefaultButton
            size="extraSmall"
            width="full"
            radius="medium"
            title={
              "Click to begin the application process for this university."
            }
          >
            Apply Now
          </DefaultButton>
        </Link>
        <WhiteButton
          size="extraSmall"
          width="full"
          radius="medium"
          onClick={handleShortlist}
          disabled={profileData?.freeUser}
          title={"Click to save the university to your shortlist."}
        >
          {data?.watchlisted ? "Remove from Shortlist" : "Add to Shortlist"}
        </WhiteButton>
      </div>
    </div>
  );
  return (
    <Popover
      content={popoverContent}
      trigger={isVisible ? "hover" : null}
      arrow={false}
      overlayInnerStyle={{
        position: "absolute",
        padding: "0px",
        top: `-15.6em`,
        right: "-9.6875em",
      }}
      onOpenChange={setIsPopoverOpen}
      getPopupContainer={(trigger) => trigger.parentElement}
    >
      <div className="university-grid" ref={cardRef}>
        <img
          src={data?.campusPic ?? Images.DefaultUniCover}
          className="w-full object-cover rounded-t-lg h-[8.75em] relative"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = Images.DefaultUniCover;
          }}
        />
        {props?.shownumber && (
          <div className="absolute w-full bg-[#0000005e] pl-5 text-white opacity-60 text-[5.75em] font-semibold  rounded-t-md flex items-center">
            {props?.index + 1}
          </div>
        )}
        <div className="p-3 flex gap-[0.625em] h-[6.25em] overflow-hidden items-center">
          <img
            src={data?.profilePic ?? Images.LogoIconOnly}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = Images.LogoIconOnly;
            }}
            className="h-[2.625em] w-[2.625em] border border-searchBorder shadow-boxContainer rounded-full object-contain bg-white"
          />
          <div className="flex flex-col gap-2">
            <div
              className="font-bold text-[0.875em] line-clamp-2 overflow-hidden text-ellipsis"
              title={data?.name}
            >
              {data?.name}
            </div>
            <div className="flex gap-1 items-center font-semibold">
              <div className="text-[0.875em] mb-1 text-main">
                <FaLocationDot />
              </div>
              <div className="flex items-center text-[0.75em]">
                {data?.city && `${data?.city}`}
                {data?.state && `, ${data?.state}`}
                {data?.country?.name && `, ${data?.country.name}`}
              </div>
            </div>
          </div>
        </div>
      </div>
      <UniversityInfoModal
        uniId={data.id}
        open={showModal}
        onCancel={() => setShowModal(false)}
      />
    </Popover>
  );
};

export default GridViewCard;
